import * as React from "react";
import { useEffect, useId, useRef, useState } from "react";
import { ArrowDownNarrowWide, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type SortOption<TKey extends string = string> = {
  key: TKey;
  label: string;
};

type SortDropdownProps<TKey extends string = string> = {
  options: SortOption<TKey>[];
  value: TKey;
  onChange: (key: TKey) => void;
  ariaLabel?: string;
};

export default function SortDropdown<TKey extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
}: SortDropdownProps<TKey>) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(() =>
    Math.max(
      0,
      options.findIndex((o) => o.key === value)
    )
  );
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const current = options.find((o) => o.key === value) ?? options[0];
  const buttonId = useId();
  const listId = `${buttonId}-listbox`;

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (
        !listRef.current?.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const item = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')[activeIndex];
    item?.focus();
  }, [open, activeIndex]);

  const select = (key: TKey) => {
    onChange(key);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActiveIndex(Math.max(0, options.findIndex((o) => o.key === value)));
      setOpen(true);
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(options.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) select(opt.key);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        className="flex items-center gap-2 whitespace-nowrap rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-2">
          <ArrowDownNarrowWide className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {current?.label}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={buttonId}
          onKeyDown={onListKeyDown}
          className="absolute right-0 top-full z-20 mt-1 min-w-[160px] rounded-lg border border-border bg-popover py-1 text-popover-foreground shadow-lg focus:outline-none"
        >
          {options.map((opt, i) => {
            const selected = value === opt.key;
            return (
              <li
                key={opt.key}
                role="option"
                aria-selected={selected}
                tabIndex={i === activeIndex ? 0 : -1}
                onClick={() => select(opt.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select(opt.key);
                  }
                }}
                className={cn(
                  "cursor-pointer px-4 py-2 text-sm outline-none transition-colors",
                  selected
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                )}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
